import { Api } from "../net/api";
import { CHAINS, TRIGGERS, type TriggerSpec } from "./triggers";

/**
 * THE FIRST-RUN TUTORIAL. Served steps, our presentation.
 *
 * 🚨 **53 AUTHORED STEPS THAT NO WEB PLAYER HAS EVER SEEN.** All three tutorial routes were served
 * and called by nothing. A new player arrived at a card game with no explanation of Foxfire, the
 * mulligan, the turn, or how to attack.
 *
 * ⭐ **ALEX ASKED FOR MORE THAN A REPLAY**, 2026-08-14: *"we need to remake the tutorial to cover our
 * Foxfire and important [mechanics] much better with zoom and pulse and interaction."* So the TEXT
 * and the ORDER are the server's - they are authored content and re-writing them here would be a
 * second owner - and the PRESENTATION is ours:
 *
 *   · SPOTLIGHT  everything but the anchor dims, so there is one thing to look at
 *   · PULSE      the anchor breathes, so the eye finds it without being told where
 *   · ZOOM       the anchor lifts slightly toward the reader
 *   · INTERACT   a step whose text asks the player to DO something has no Next button, and
 *                advances when they do it
 *
 * ⭐ **THE ORDER IS THE `NextId` CHAIN, NOT THE ARRAY.** The served list is unordered - `23_Session_
 * Hand` is first in the file and `0_Lobby_Start` is second. Walking the array would teach the game
 * backwards.
 *
 * ⚠️ **A STEP WITH NO RESOLVABLE ANCHOR IS SHOWN CENTRED, NEVER SKIPPED.** Its text is still the
 * lesson. Skipping it because a selector missed would silently un-teach a mechanic - which is the
 * failure mode the whole file exists to end.
 *
 * ⚠️ **AND THE RUNNER NEVER BLOCKS THE GAME.** If a step cannot advance (its event never arrives,
 * its anchor never appears), it times out and moves on rather than trapping the player behind a
 * popup. A tutorial that can wedge the client is worse than no tutorial.
 */

interface Popup {
  Title?: string;
  Text?: string;
  ContinueButtonText?: string;
}

interface StepData {
  NextId?: string;
  KeywordTriggers?: string[];
  PauseRequired?: boolean;
  DelayBeforeInvoke?: number;
}

interface Step {
  Id?: string;
  Data?: StepData;
  Popup?: Popup;
}

/**
 * The step the FIRST chain starts from.
 *
 * ⚠️ **THERE ARE THREE CHAINS AND THIS IS ONLY THE FIRST ONE'S HEAD.** `CHAINS` in `./triggers.ts`
 * is the owner; running from here alone reaches 2 of 53 steps.
 */
const FIRST = CHAINS[0]!.start;

/** A step that cannot advance gives up after this and moves on, rather than wedging the client. */
const STALL_MS = 45_000;

let steps: Map<string, Step> | null = null;

export async function loadTutorial(): Promise<Map<string, Step>> {
  if (steps) return steps;
  const raw = await Api.tutorialConfig() as Array<{ id?: string; data?: string }>;
  const map = new Map<string, Step>();
  for (const row of raw ?? []) {
    if (!row?.data) continue;
    try {
      const parsed = JSON.parse(row.data) as Step;
      if (row.id) map.set(row.id, parsed);
    } catch {
      // ⚠️ REPORTED, never silent. A step that will not parse is a lesson the player loses, and a
      // tutorial quietly one step shorter is exactly the class of defect this file exists to end.
      console.warn(`[tutorial] step ${row.id} has unparseable data and will be skipped`);
    }
  }
  steps = map;
  return map;
}

/**
 * Is this step asking the player to DO something?
 *
 * ⭐ **THE ORIGINAL ENCODES IT AS AN EMPTY CONTINUE BUTTON**, not as a flag: "Drag & Drop ... on to
 * the board" and "Click on the button to end your turn" both carry `ContinueButtonText: ""`, while
 * every read-and-continue step carries "Next". So the presence of button text IS the distinction,
 * and it comes from the content rather than from a list of step ids I would have to maintain.
 */
function isInteractive(p: Popup | undefined): boolean {
  return typeof p?.ContinueButtonText === "string" && p.ContinueButtonText.trim() === "";
}

function specFor(trigger: string): TriggerSpec | undefined {
  return TRIGGERS[trigger];
}

/**
 * Does this step exist only to explain something we cut?
 *
 * ⚠️ **"ALL of its anchors are cut", not "any".** A step that points at both a live control and a
 * cut one still has something true to say, and dropping it would lose that. Only a step whose every
 * anchor is a declared cut has nothing left to teach.
 *
 * ⚠️ Steps with no anchors at all (pure text, or engine-event waits) are NEVER caught by this - they
 * are the ones that explain the game itself.
 */
function teachesCutFeature(step: Step): boolean {
  const anchors = (step.Data?.KeywordTriggers ?? [])
    .map(specFor)
    .filter((s): s is TriggerSpec => !!s && (!!s.sel || !!s.skip));
  return anchors.length > 0 && anchors.every((s) => !!s.skip);
}

/** The element a step points at, or null for a centred popup. */
function anchorFor(step: Step): HTMLElement | null {
  for (const t of step.Data?.KeywordTriggers ?? []) {
    const spec = specFor(t);
    if (!spec?.sel) continue;
    const el = document.querySelector(spec.sel);
    if (el instanceof HTMLElement) return el;
  }
  return null;
}

export class TutorialRunner {
  private layer: HTMLElement | null = null;
  private current: string | null = null;
  private stopped = false;
  private lastAnchor: HTMLElement | null = null;

  /** Signals the runner is waiting on, by step id. Fed from the match hub. */
  private waiting: { event: string; resolve: () => void } | null = null;

  /**
   * An engine signal arrived. Advances a step that was waiting for it.
   *
   * ⚠️ Called for EVERY hub signal; matching is by name, so an unrelated signal is ignored rather
   * than advancing the tutorial past a lesson.
   */
  onSignal(name: string): void {
    if (this.waiting && this.waiting.event === name) {
      const w = this.waiting;
      this.waiting = null;
      w.resolve();
    }
  }

  stop(): void {
    this.stopped = true;
    this.clear();
  }

  private clear(): void {
    this.layer?.remove();
    this.layer = null;
    if (this.lastAnchor) {
      this.lastAnchor.classList.remove("tut-anchor");
      this.lastAnchor = null;
    }
  }

  async run(from: string = FIRST): Promise<void> {
    const all = await loadTutorial();
    let id: string | undefined = from;
    const seen = new Set<string>();

    while (id && !this.stopped) {
      // ⚠️ A CHAIN CAN LOOP. `NextId` is data, and data can point backwards; without this the
      // runner would spin forever on a content mistake.
      if (seen.has(id)) {
        console.warn(`[tutorial] chain loops at ${id} - stopping`);
        break;
      }
      seen.add(id);

      const step: Step | undefined = all.get(id);
      if (!step) {
        console.warn(`[tutorial] step ${id} is referenced and does not exist`);
        break;
      }
      this.current = id;
      // ⭐ **A STEP THAT TEACHES A CUT FEATURE IS SKIPPED. A STEP WHOSE SELECTOR MISSED IS NOT.**
      // Those look identical from here and are opposites:
      //
      //   · `skip` is a DECLARED decision - the Ironside layer is cut, so "This is the Quadrant
      //     allegiance of your deck, which is determined by the Ironside selected" is a lesson about
      //     a thing that does not exist. Showing it MISINFORMS, which is worse than silence.
      //   · a selector that resolves to nothing is a BUG in the map, and dropping the step there
      //     would silently un-teach a real mechanic - so those are shown centred instead.
      //
      // Caught live: the lobby tour opened on `61_Lobby_DecksIronsideFlag`, explaining Ironsides to
      // a player who will never see one.
      if (teachesCutFeature(step)) {
        console.info(`[tutorial] skipping ${id} - it teaches a feature that is cut`);
        id = step.Data?.NextId;
        continue;
      }
      await this.show(step);
      id = step.Data?.NextId;
    }
    this.clear();
    // Record where the player got to, so a refresh does not restart the lesson.
    try {
      await Api.tutorialPostProgress({ lastStepId: this.current ?? "", completed: !id });
    } catch {
      // ⚠️ Not fatal: a tutorial that refuses to end because a progress write failed is worse than
      // one a player may see twice.
    }
  }

  /** Draw one step and wait for whatever advances it. */
  private async show(step: Step): Promise<void> {
    const popup = step.Popup;
    const title = String(popup?.Title ?? "").trim();
    const text = String(popup?.Text ?? "").trim();

    // ⭐ **A STEP WITH NO WORDS IS A LOGIC STEP** - the original uses them to wait for an engine
    // signal (`13_Session_Mulligan_Logic`, `30_Session_SleepingEffect_Logic`). Nothing is drawn;
    // the runner just waits.
    if (!title && !text) {
      await this.waitForEvent(step);
      return;
    }

    await this.paint(step, title, text);

    if (isInteractive(popup)) {
      // The player must DO the thing. Advance on the engine signal, or when the anchor stops
      // existing (they played the card it was pointing at), whichever comes first.
      await Promise.race([this.waitForEvent(step), this.waitForAnchorGone(step), sleep(STALL_MS)]);
    } else {
      await Promise.race([this.waitForClick(), sleep(STALL_MS)]);
    }
    this.clear();
  }

  private async paint(step: Step, title: string, text: string): Promise<void> {
    this.clear();
    // ⭐ **A TOUR HAS TO WALK.** The lobby chain visits the menu, the deck builder, duels and the
    // leagues screen; each anchor declares which screen it lives on. Without this the runner sat on
    // the menu explaining the deck builder and every anchor resolved to nothing - a spotlight on
    // an empty screen, which reads as the tutorial being broken rather than as a missing route.
    await this.goToScreenFor(step);
    const anchor = anchorFor(step);
    this.lastAnchor = anchor;

    const layer = document.createElement("div");
    layer.className = "tut";
    layer.dataset["step"] = String(step.Id ?? "");

    // ⭐ THE SPOTLIGHT. Everything dims except the anchor, which is lifted above the scrim by a
    // z-index rather than by being re-parented - moving a live control into an overlay would break
    // whatever the game had bound to it.
    const scrim = document.createElement("div");
    scrim.className = "tut__scrim";
    layer.append(scrim);

    const box = document.createElement("div");
    box.className = "tut__box";
    if (title) {
      const h = document.createElement("h2");
      h.className = "tut__title";
      h.textContent = title;
      box.append(h);
    }
    if (text) {
      const p = document.createElement("p");
      p.className = "tut__text";
      // ⚠️ The original's text carries Unity rich-text (`<color=#E6B579>...</color>`). It is CONTENT,
      // so it is not rewritten server-side - it is translated here, to a span, and anything else is
      // stripped rather than injected.
      p.append(...richText(text));
      box.append(p);
    }

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "utilbtn tut__next";
    const label = String(step.Popup?.ContinueButtonText ?? "").trim();
    if (isInteractive(step.Popup)) {
      // ⭐ NO BUTTON. The step is asking for an action, and a Next button beside "drag the card onto
      // the board" invites the player to skip the only part that teaches anything.
      const hint = document.createElement("span");
      hint.className = "tut__do";
      hint.textContent = "Try it";
      box.append(hint);
    } else {
      // ⚠️ **A MISSING LABEL IS "NEXT", NOT AN EMPTY BUTTON.** `24b_Session_FoxfireTypes` was added
      // by an earlier session with `ContinueButtonText: null`, which rendered a button with no
      // words on the step that explains our headline mechanic.
      btn.textContent = label || "NEXT";
      btn.dataset["label"] = btn.textContent;
      box.append(btn);
    }
    layer.append(box);
    document.body.append(layer);
    this.layer = layer;

    if (anchor) {
      anchor.classList.add("tut-anchor");
      position(box, anchor);
    } else {
      box.classList.add("tut__box--centre");
    }
  }

  /**
   * Route to the screen this step's anchor lives on, and wait for it to arrive.
   *
   * ⚠️ **NEVER NAVIGATES DURING A MATCH.** A `match` anchor means "the board", and the player is
   * already there; routing would abandon a live game to show a popup. The router stamps
   * `data-screen`, so arrival is observed rather than guessed at with a sleep.
   */
  private async goToScreenFor(step: Step): Promise<void> {
    const want = (step.Data?.KeywordTriggers ?? [])
      .map(specFor).find((s) => s?.sel && s.screen)?.screen;
    if (!want || want === "match") return;
    const here = document.documentElement.dataset["screen"] ?? "";
    if (here === want) return;
    location.hash = `#/${want}`;
    for (let i = 0; i < 40; i++) {
      if ((document.documentElement.dataset["screen"] ?? "") === want) break;
      await sleep(50);
    }
    // A beat for the screen's own content to render before we point at part of it.
    await sleep(250);
  }

  private waitForClick(): Promise<void> {
    return new Promise((resolve) => {
      const b = this.layer?.querySelector(".tut__next");
      if (!(b instanceof HTMLElement)) { resolve(); return; }
      b.addEventListener("click", () => resolve(), { once: true });
    });
  }

  private waitForEvent(step: Step): Promise<void> {
    const events = (step.Data?.KeywordTriggers ?? []).filter((t) => specFor(t)?.event);
    if (!events.length) return sleep(600);
    return new Promise((resolve) => { this.waiting = { event: events[0]!, resolve }; });
  }

  /** The anchor disappearing means the player did the thing it was pointing at. */
  private waitForAnchorGone(step: Step): Promise<void> {
    const el = anchorFor(step);
    if (!el) return sleep(STALL_MS);
    return new Promise((resolve) => {
      const t = setInterval(() => {
        if (!el.isConnected) { clearInterval(t); resolve(); }
      }, 250);
    });
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Unity rich text -> DOM nodes. Only `<color=#hex>` is honoured; everything else is stripped.
 *
 * ⚠️ **NEVER `innerHTML`.** This text is served, and served text rendered as markup is an injection
 * hole even when we author it today.
 */
function richText(src: string): Node[] {
  const out: Node[] = [];
  const re = /<color=(#[0-9A-Fa-f]{3,8})>([\s\S]*?)<\/color>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) out.push(document.createTextNode(strip(src.slice(last, m.index))));
    const span = document.createElement("span");
    span.style.color = m[1]!;
    span.textContent = strip(m[2]!);
    out.push(span);
    last = re.lastIndex;
  }
  if (last < src.length) out.push(document.createTextNode(strip(src.slice(last))));
  return out;
}

function strip(s: string): string {
  return s.replace(/<[^>]*>/g, "");
}

/**
 * Put the box where it does not cover the thing it is explaining.
 *
 * ⚠️ **THE FIRST VERSION PUT IT AT THE ANCHOR'S TOP-LEFT AND IT COVERED THE SCREEN.** Pointing at
 * `.deck__collection` - a 1066x413 grid - there was no room below, so it flipped ABOVE and landed
 * over the deck tabs: a tutorial explaining the deck builder while hiding it. A big anchor has no
 * "beside", so the rule has to change with the anchor's size rather than always flipping.
 *
 * ⭐ **PICK THE LARGEST EMPTY BAND AROUND THE ANCHOR.** Above, below, left, right - whichever has
 * the most room takes the box. That is what a person does with a sticky note, and it degrades
 * sensibly on any window size instead of having a favourite side.
 */
const BOX_W = 360;
const BOX_H = 190;
const GAP = 16;

function position(box: HTMLElement, anchor: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  box.style.position = "fixed";
  box.style.bottom = "";
  box.style.right = "";

  const bands = [
    { name: "below", space: vh - r.bottom - GAP },
    { name: "above", space: r.top - GAP },
    { name: "right", space: vw - r.right - GAP },
    { name: "left", space: r.left - GAP },
  ].sort((a, b) => b.space - a.space);
  const best = bands[0]!;

  const clampX = (x: number): number => Math.max(GAP, Math.min(x, vw - BOX_W - GAP));
  const clampY = (y: number): number => Math.max(GAP, Math.min(y, vh - BOX_H - GAP));

  if (best.name === "below") {
    box.style.top = `${clampY(r.bottom + GAP)}px`;
    box.style.left = `${clampX(r.left + r.width / 2 - BOX_W / 2)}px`;
  } else if (best.name === "above") {
    box.style.top = `${clampY(r.top - GAP - BOX_H)}px`;
    box.style.left = `${clampX(r.left + r.width / 2 - BOX_W / 2)}px`;
  } else if (best.name === "right") {
    box.style.left = `${clampX(r.right + GAP)}px`;
    box.style.top = `${clampY(r.top + r.height / 2 - BOX_H / 2)}px`;
  } else {
    box.style.left = `${clampX(r.left - GAP - BOX_W)}px`;
    box.style.top = `${clampY(r.top + r.height / 2 - BOX_H / 2)}px`;
  }

  // ⚠️ **AN ANCHOR THAT FILLS THE SCREEN LEAVES NO BAND AT ALL.** Rather than covering it at a
  // random edge, the box goes to the bottom centre, where a caption belongs.
  if (best.space < BOX_H * 0.6) {
    box.style.top = `${vh - BOX_H - GAP * 2}px`;
    box.style.left = `${clampX(vw / 2 - BOX_W / 2)}px`;
  }
}
